import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { loadYaml, type YamlContent, type YamlSlug } from "../content/yamlLoader";
import packageInfo from "../../package.json";

const legalLinks: Array<{ slug: YamlSlug; label: string }> = [
  { slug: "kontakt", label: "Kontakt" },
  { slug: "rechtliches", label: "Rechtliches" },
  { slug: "datenschutz", label: "Datenschutz" },
  { slug: "impressum", label: "Impressum" }
];

const fallbackTitles: Record<YamlSlug, string> = {
  kontakt: "Kontakt",
  rechtliches: "Rechtliches",
  datenschutz: "Datenschutz",
  impressum: "Impressum"
};

export function LegalLinks({ placement }: { placement: "sidebar" | "settings" | "unlock" }) {
  const [activeSlug, setActiveSlug] = useState<YamlSlug | null>(null);

  return (
    <>
      <div className={`legal-links legal-links-${placement}`}>
        {legalLinks.map((link) => (
          <button type="button" key={link.slug} onClick={() => setActiveSlug(link.slug)}>
            {link.label}
          </button>
        ))}
        <span className="legal-version">v{packageInfo.version}</span>
      </div>
      <LegalModal slug={activeSlug} onClose={() => setActiveSlug(null)} />
    </>
  );
}

function LegalModal({ slug, onClose }: { slug: YamlSlug | null; onClose: () => void }) {
  const [content, setContent] = useState<YamlContent | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setError("");
    setContent(null);
    loadYaml(slug)
      .then(setContent)
      .catch(() => setError("Die Inhalte konnten nicht geladen werden."))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!slug) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [slug, onClose]);

  if (!slug) return null;

  return createPortal(
    <div className="modal legal-modal" onClick={onClose}>
      <div className="modal-panel legal-modal-panel" onClick={(event) => event.stopPropagation()}>
        <div className="legal-modal-header">
          <h2>{content?.title ?? fallbackTitles[slug]}</h2>
          <button type="button" className="button small" onClick={onClose} aria-label="Legal-Dialog schliessen">
            <X /> Schliessen
          </button>
        </div>
        <div className="legal-modal-body">
          {loading && <p>Wird geladen...</p>}
          {error && <p className="danger-text">{error}</p>}
          {!loading && !error && <pre>{content?.text}</pre>}
        </div>
      </div>
    </div>,
    document.body
  );
}
